import { useEffect, useRef } from 'react';

/**
 * Driver's-license barcode auto-fill. US/CA licenses carry a PDF417 barcode encoding AAMVA data; a
 * keyboard-wedge barcode scanner (e.g. Teemi) "types" that data as a fast keystroke burst beginning
 * with "@". We detect the burst, buffer it, and parse the AAMVA element codes into demographic fields
 * — mirroring the v1 capture flow. No device/driver needed: the scanner presents as a USB keyboard.
 */

/** Map of AAMVA element id (e.g. "DCS") → value, parsed from the raw keystroke buffer. */
function toMap(raw: string): Record<string, string> {
  const map: Record<string, string> = {};
  for (const line of raw.split(/\r?\n/)) {
    const t = line.trim();
    if (t.length >= 3 && /^[A-Z]{3}$/.test(t.slice(0, 3).toUpperCase())) {
      map[t.slice(0, 3).toUpperCase()] = t.slice(3).trim();
    }
  }
  return map;
}

/**
 * AAMVA eye-colour code (DAY) → the NCIC code the EBTS field 2.031 (EYE) carries.
 *
 * Ported from v1's `eyeColorMap`, which built the same table into every one of its five workflows. Most
 * codes are identical in both vocabularies and pass straight through; three are not, and those three are
 * the whole reason a table exists rather than a direct assignment.
 *
 * In CODE, not in the workflow definition. A stored workflow REPLACES its default wholesale — there is no
 * field-level merge — so a table shipped as workflow data would never reach a customer who had ever saved
 * a configuration, which is every customer who has been set up.
 */
const EYE_FROM_AAMVA: Readonly<Record<string, string>> = {
  BLK: 'BLK', BLU: 'BLU', BRO: 'BRO', GRY: 'GRY', GRN: 'GRN', HAZ: 'HAZ', MAR: 'MAR', PNK: 'PNK',
  // The three that differ: AAMVA's "unknown" and "dichromatic" have other names under NCIC, and some
  // jurisdictions still encode hazel as HL.
  UNK: 'XXX', DIC: 'MUL', HL: 'HAZ',
};

/** AAMVA hair-colour code (DAZ) → the NCIC code for EBTS field 2.032 (HAI). See EYE_FROM_AAMVA. */
const HAIR_FROM_AAMVA: Readonly<Record<string, string>> = {
  BAL: 'BAL', BLK: 'BLK', BLN: 'BLN', BRO: 'BRO', GRY: 'GRY', RED: 'RED', SDY: 'SDY', WHI: 'WHI',
  UNK: 'XXX',
};

/** AAMVA date (DBB) is 8 digits — CA is YYYYMMDD, US is MMDDYYYY. Return ISO YYYY-MM-DD for date inputs. */
function parseDob(val: string): string | undefined {
  if (!/^\d{8}$/.test(val)) return undefined;
  const yearFirst = new Date().getFullYear() - parseInt(val.slice(0, 4), 10) < 150;
  const [y, m, d] = yearFirst
    ? [val.slice(0, 4), val.slice(4, 6), val.slice(6, 8)]
    : [val.slice(4, 8), val.slice(0, 2), val.slice(2, 4)];
  return `${y}-${m}-${d}`;
}

/** Just enough of a field definition to check a coded value against what the operator can actually pick. */
export interface ScannableField {
  name: string;
  options?: { value: string; label: string }[] | undefined;
}

/**
 * Drop anything the workflow in front of the operator cannot hold.
 *
 * Workflows are runtime data: a customer's saved configuration may offer a shorter list of eye colours
 * than the shipped default, and writing a value its select has no option for would leave the field looking
 * EMPTY while holding something — the operator sees a blank box, submits, and the EFT carries a colour
 * nobody chose. Better to leave it for them to pick.
 *
 * Fields the workflow does not define at all are dropped for the same reason: an invisible value is worse
 * than an absent one.
 */
export function applicableTo(
  patch: Record<string, string>,
  fields: readonly ScannableField[] | undefined,
): Record<string, string> {
  if (!fields) return patch;
  const byName = new Map(fields.map((f) => [f.name, f]));
  const out: Record<string, string> = {};
  for (const [name, value] of Object.entries(patch)) {
    const f = byName.get(name);
    if (!f) continue;
    if (f.options && !f.options.some((o) => o.value === value)) continue;
    out[name] = value;
  }
  return out;
}

/**
 * Parse an AAMVA keystroke buffer into a demographic-field patch keyed by the v2 workflow field names.
 *
 * Eye and hair colour are TRANSLATED, not copied: the barcode speaks AAMVA and the EFT field speaks NCIC.
 * They were previously skipped altogether — the comment here said coded fields were "left for the operator
 * to select" — so a scan that filled in the name and date of birth left two required fields blank, and
 * every operator moving from v1 had to type them back in on every capture.
 *
 * The remaining coded fields (race, place of birth) are still left alone; see the note in the commit that
 * added this.
 */
export function parseAamva(raw: string): Record<string, string> {
  const m = toMap(raw);
  const patch: Record<string, string> = {};
  const set = (k: string, v: string | undefined) => { if (v) patch[k] = v; };
  /*
   * Coded values are uppercased before lookup. v1 uppercased EVERY scanned value as it tokenised; this
   * parser does not, because it keeps the name as printed — so the normalising has to happen here, where
   * a lowercase `bro` would otherwise miss the table and silently produce nothing.
   */
  const coded = (v: string | undefined) => (v ?? '').trim().toUpperCase();

  if (m['DCS']) set('lastName', m['DCS']);
  if (m['DAC']) {
    const [first, ...rest] = m['DAC'].split(/\s+/);
    set('firstName', first);
    if (rest.length && rest[0]) set('middleName', rest[0].slice(0, 1));
  }
  if (m['DAD'] && !patch['middleName']) set('middleName', m['DAD'].slice(0, 1));
  if (m['DCU']) set('suffix', m['DCU']);
  if (m['DBB']) set('dateOfBirth', parseDob(m['DBB']));
  if (m['DBC'] === '1') set('sex', 'M');
  else if (m['DBC'] === '2') set('sex', 'F');
  if (m['DAW'] && /^\d+$/.test(m['DAW'])) set('weight', m['DAW']);
  if (m['DAY']) set('eyeColor', EYE_FROM_AAMVA[coded(m['DAY'])]);
  if (m['DAZ']) set('hairColor', HAIR_FROM_AAMVA[coded(m['DAZ'])]);

  return patch;
}

/** How many recognized AAMVA elements a buffer must contain to be treated as a real scan. */
function elementCount(raw: string): number {
  return Object.keys(toMap(raw)).length;
}

interface ScanResult { patch: Record<string, string>; fields: number }

/**
 * Listen for a keyboard-wedge license scan while `enabled`. On a completed burst that yields fields,
 * calls onScan with the field patch; if a burst is detected but unreadable, calls onError (scanner
 * likely not in AAMVA/keyboard mode). The AAMVA burst begins with "@" and arrives far faster than human
 * typing, so ordinary form entry is never affected.
 */
export function useLicenseScanner(
  enabled: boolean,
  onScan: (r: ScanResult) => void,
  onError?: () => void,
  /** The workflow's fields, so a scanned value is only applied where the operator could have picked it. */
  fields?: readonly ScannableField[] | undefined,
): void {
  const streaming = useRef(false);
  const buffer = useRef('');
  const lastKey = useRef('');
  const lastMs = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the latest callbacks without re-binding the listener.
  const onScanRef = useRef(onScan);
  const onErrorRef = useRef(onError);
  const fieldsRef = useRef(fields);
  onScanRef.current = onScan;
  onErrorRef.current = onError;
  // A ref, not a dependency: the workflow loads after this hook first runs, and re-binding the key
  // listener on every render of the demographics step would drop a burst already in progress.
  fieldsRef.current = fields;

  useEffect(() => {
    if (!enabled) return;

    const finish = () => {
      streaming.current = false;
      const raw = buffer.current;
      buffer.current = '';
      if (timer.current) { clearTimeout(timer.current); timer.current = null; }
      if (elementCount(raw) > 2) {
        onScanRef.current({ patch: applicableTo(parseAamva(raw), fieldsRef.current), fields: elementCount(raw) });
      }
      else if (raw.length > 3) onErrorRef.current?.();
    };

    const append = (key: string) => {
      if (key === 'Enter') buffer.current += '\n';
      else if (key.length === 1) buffer.current += key;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(finish, 140); // burst idle → done
    };

    const onKey = (event: KeyboardEvent) => {
      const now = Date.now();
      const delta = lastMs.current ? now - lastMs.current : undefined;
      lastMs.current = now;

      if (!streaming.current) {
        // Start of a scan: a fast keystroke right after a "@" (AAMVA compliance indicator).
        if (delta !== undefined && delta < 50 && lastKey.current === '@') {
          streaming.current = true;
          buffer.current = '@';
          // The "@" already landed in the focused field before we detected the burst — strip it.
          const el = event.target;
          if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
            if (el.value.endsWith('@')) el.value = el.value.slice(0, -1);
          }
          append(event.key);
          event.preventDefault();
          event.stopPropagation();
        }
      } else {
        if (event.key !== 'Shift' && event.key !== 'Control' && event.key !== 'Alt' && event.key !== 'Meta') {
          append(event.key);
        }
        event.preventDefault();
        event.stopPropagation();
      }
      lastKey.current = event.key;
    };

    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      if (timer.current) clearTimeout(timer.current);
      streaming.current = false;
      buffer.current = '';
    };
  }, [enabled]);
}
